/**
 * ADM-INTEGRATION-ENTRA.MODAL
 * Load users from Entra ID (Cryptr), display the fields found then save the list
 */
import { store } from "@/index"
import { v4 as uuid } from "uuid"
import { useEffect, useState } from "react"
import { filterFetch } from "@/redux/filter.actions"
import { Filter } from "@/redux/filter.types"
import { integrationSyncEntra } from "@/redux/integration.actions"
import { User } from "@/redux/user.types"
import ImportModal from "./import.modal"
import LoadingModal from "./loading.modal"
import Modal from "@/components/modal"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Tooltip from "@/components/tooltip"
import Checkbox from "@/components/checkbox"
import ListFields from "@/components/list-fields"

interface OwnProps {
  onClose: Function
  onNext: Function
}

interface EntraField {
  importName: string
  name: string
  count: number
  sample: string | null
}

function AdmIntegrationEntraModal(props: OwnProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isImporting, setIsImporting] = useState(false)
  const [fields, setFields] = useState<EntraField[]>([])
  const [filtersToImport, setFiltersToImport] = useState<Filter[]>([])
  const [usersToImport, setUsersToImport] = useState<User[]>([])
  const [isObserversCreated, setIsObserversCreated] = useState(true)

  //On load fetch Entra users (already formatted by the backend)
  useEffect(() => {
    load()

    async function load() {
      const filtersResponse: any = await store.dispatch(filterFetch(false, false))
      const entraResponse: any = await store.dispatch(integrationSyncEntra())

      if (entraResponse.error || filtersResponse.error) {
        props.onClose()
        return
      }

      //Keep the id of the existing filters
      setFiltersToImport(
        entraResponse.filters.map((filter) => {
          const currentFilter = filtersResponse.find((x) => x.importName === filter.importName)
          return new Filter({ ...filter, id: currentFilter ? currentFilter.id : uuid() })
        })
      )
      setUsersToImport(entraResponse.users.map((user) => new User(user)))
      setFields(entraResponse.fields)
      setIsLoading(false)
    }
  }, [])

  const hasManager = fields.some((field) => field.importName === "manager" && field.count)

  //Managers become observers of their branch, else the populations computed by the backend are ignored
  function next() {
    setUsersToImport(
      usersToImport.map((user) =>
        isObserversCreated && hasManager && user.populationsObserver.length
          ? new User({ ...user, role: "OBSERVER" })
          : new User({ ...user, populationsObserver: [] })
      )
    )
    setIsImporting(true)
  }

  return isLoading ? (
    <LoadingModal />
  ) : isImporting ? (
    <ImportModal
      isFromIntegration
      filters={filtersToImport}
      users={usersToImport}
      onClose={() => props.onClose()}
      onNext={() => props.onNext()}
    />
  ) : (
    <Modal
      isCloseButtonVisible
      isLarge
      onClose={() => props.onClose()}
      onNext={next}
      title="Champs importés depuis Entra ID">
      <Tooltip text={hasManager ? null : "L'attribut Manager n'est renseigné pour aucun collaborateur."}>
        <Checkbox
          active={isObserversCreated && hasManager}
          disabled={!hasManager}
          onClick={() => setIsObserversCreated(!isObserversCreated)}
          text="Utiliser l'attribut manager pour créer les accès observateurs."
        />
      </Tooltip>

      <div className="h-10" />

      <ListFields>
        <ListCell text="Attribut" width={200} />
        <ListCell isFlex text="Valeurs" />
      </ListFields>

      {fields.map((field) => (
        <ListItem key={field.importName}>
          {field.count ? (
            <>
              <ListCell isBold text={field.name} width={200} />
              <ListCell isFlex text={field.sample} />
            </>
          ) : (
            <Tooltip text="Ce champ n'est renseigné pour aucun collaborateur.">
              <ListCell className="grey-t" isBold text={field.name} width={200} />
            </Tooltip>
          )}
        </ListItem>
      ))}
    </Modal>
  )
}

export default AdmIntegrationEntraModal
